<script>
import fileSize from 'filesize'
import uploadcare from 'uploadcare-widget/uploadcare.min.js'

export default {
  name: 'FileUploader',
  props: {
    multiple: {
      type: Boolean,
      default: false,
    },
    multipleMin: {
      type: [Number, String],
      default: 1,
    },
    multipleMax: {
      type: [Number, String],
      default: 0,
    },
    inputAcceptTypes: {
      type: [String, Array],
      default: null,
    },
    sizeMax: {
      type: Number,
      default: null,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    defaultTabs: {
      type: String,
      default: '',
    },
    buttonType: {
      type: String,
      default: 'button',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uploadedFiles: [],
      credentials: {
        publicKey: process.env.VUE_APP_UPLOADCARE_PUBLIC_KEY,
      },
      preview: null,
      size: null,
      loading: false,
    }
  },
  computed: {
    tabs() {
      return this.defaultTabs || 'file url camera dropbox gdrive box skydrive'
    },
    options() {
      const { tabs, multiple, multipleMin, multipleMax, uploadedFiles } = this
      const { publicKey } = this.credentials

      return {
        publicKey,
        multiple,
        multipleMin,
        multipleMax: multipleMax ? multipleMax - uploadedFiles.length : 0,
        tabs,
        validators: [
          this.fileTypesVaidator,
          this.checkSize,
        ],
      }
    },
    buttonDisabled() {
      if (this.disabled) return true
      if (this.multiple) return parseInt(this.multipleMax) ? (this.uploadedFiles.length >= this.multipleMax) : false
      return !!this.uploadedFiles.length
    },
    buttonText() {
      const { multiple, multipleMax, uploadedFiles } = this
      if (multiple) {
        if (multipleMax) {
          return `Select files (${multipleMax - uploadedFiles.length} left)`
        } else return 'Select files'
      } else return 'Select file'
    },
  },
  watch: {
    uploadedFiles(val) {
      this.$emit('change', val)
    },
  },
  methods: {
    onClick() {
      this.fileGroup = uploadcare.openDialog(null, this.options)

      this.fileGroup.done(filePromise => {
        this.loading = true
        if (this.multiple) {
          const files = filePromise.files()
          files.forEach(file => {
            file.done(this.onSuccessUpload)
            file.fail(err => {
              this.$emit('error', err)
              this.loading = false
            })
          })
        } else {
          filePromise.done(this.onSuccessUpload)
          filePromise.fail(err => {
            this.$emit('error', err)
            this.loading = false
          })
        }
      })

      this.fileGroup.fail(err => this.$emit('error', err))
    },
    fileTypesVaidator(file) {
      const name = file.name
      const extRE = /[^.]+$/
      const extension = extRE.exec(name)[0]

      if (this.inputAcceptTypes.indexOf(extension.toLowerCase()) < 0) {
        throw new Error('Wrong file format!')
      }
    },
    async checkSize({ size }) {
      if (!size || !this.sizeMax) {
        return
      }

      const sizeMax = this.sizeMax * 1024

      if (sizeMax < size) {
        this.fileGroup && this.fileGroup.reject()
        await this.$nextTick()
        this.$notify({
          type: 'error',
          title: 'Error',
          message: `Maximum file size: ${fileSize(sizeMax)}.
          Your file size is ${fileSize(size)}`,
        })
        throw new Error('size')
      }
    },
    onDeleteClick(index) {
      const deletedFile = this.uploadedFiles.splice(index, 1)
      this.$emit('deleted-file', deletedFile)
    },
    onSuccessUpload(file) {
      this.uploadedFiles.push(file)
      this.loading = false
      this.$emit('input', this.uploadedFiles)
    },
  },
}
</script>

<template>
  <div>
    <div :class="$style.files">
      <div
        v-for="(file, index) in uploadedFiles"
        :key="index"
        :class="$style.file"
      >
        <div :class="$style.fileName">
          {{ file.name }}
        </div>
        <div
          v-if="canDelete"
          :class="$style.fileDeleteIcon"
        >
          <el-button
            icon="el-icon-delete"
            size="small"
            :class="$style.red"
            circle
            @click="onDeleteClick(index)"
          />
        </div>
      </div>
    </div>

    <div>
      <el-button
        :type="buttonType"
        plain
        :class="$style.uploaderButton"
        :disabled="buttonDisabled"
        :loading="loading"
        @click.prevent="onClick"
      >
        <slot name="button-text">
          {{ buttonText }}
        </slot>
      </el-button>
    </div>
  </div>
</template>

<style lang="scss" module>
.files {
  margin-bottom: 0.5rem;
}

.file {
  display: flex;
  align-items: center;
  margin-top: 0.2rem;
  margin-bottom: 0.2rem;
}

.fileName {
  font-weight: bold;
}

.fileDeleteIcon {
  margin-left: 1rem;
}

.red {
  color: red;
}
</style>
